// Copyright 2023, Durable
import React, { useCallback, useContext, useMemo, useState } from "react";
import {
  DoNotShowForJob,
  DoNotShowForPage,
  NotificationService,
  UserNotification
} from "@durable/durable-client";

import { useProfileContext } from "./components/shared/Auth";
import isEqual from "lodash.isequal";
import { logging } from "./logging";
import { useEventSource } from "./shared/durableEventSource";

export type NotificationContextType = {
  allNotifications: Array<UserNotification>;
  unreadNotifications: Array<UserNotification>;
  refreshNotifications: () => Promise<boolean>;
  deleteNotification: (id: string) => Promise<boolean>;
  markNotificationRead: (id: string) => Promise<boolean>;
  startNativeNotifications: () => Promise<boolean>;
  sendNativeNotification: (title: string) => Promise<boolean>;
  getFilteredNotifications: (
    currentJobId?: string
  ) => Array<UserNotification>;
  setPageProperties: (arg: Array<DoNotShowForJob | DoNotShowForPage>) => void;
};

const NotificationContext =
  React.createContext<NotificationContextType>(null);

/**
 * Creates a context provider for functions and state relating to the global
 * notification system, providing easy global access to the notifications.
 *
 * All functions in the provider must return promises so they may be used
 * asynchronously.
 */
export function NotificationProvider({ children }: { children }) {
  const [allNotifications, setAllNotifications] =
    useState<Array<UserNotification>>(null);
  const [unreadNotifications, setUnreadNotifications] =
    useState<Array<UserNotification>>(null);
  const [pushPermission, setPushPermission] =
    useState<NotificationPermission>(null);
  const [pageProperties, setPageProperties] =
    useState<Array<DoNotShowForJob | DoNotShowForPage>>(null);

  const profileContext = useProfileContext();

  // Fetch new notifications and sort them into required state objects.
  const refreshNotifications = useCallback(async () => {
    const newNotifications = await NotificationService.getAllNotifications(
      true
    );
    // This should sort by date instead of simple added order
    newNotifications.reverse();
    setAllNotifications(newNotifications);
    const unread = newNotifications.filter(
      (notification) => notification.read != true
    );
    setUnreadNotifications(unread);
    return true;
  }, []);

  // If an OS supports it, hook into the web notifications API.
  const startNativeNotifications = useCallback(async () => {
    let newPushPermission = null;
    if ("Notification" in window) {
      newPushPermission = await Notification.requestPermission();
      setPushPermission(newPushPermission);
    } else {
      logging.info("Browser does not support desktop notifications");
      setPushPermission("denied");
    }
    return newPushPermission == "granted";
  }, []);

  // If the user has allowed it, send a notification with a given title and body
  const sendNativeNotification = useCallback(
    async (title: string) => {
      if (pushPermission == "granted") {
        new Notification(title);
        return true;
      } else {
        logging.warning(
          "Notification permission denied, cannot send notification"
        );
        startNativeNotifications();
        return false;
      }
    },
    [pushPermission, startNativeNotifications]
  );

  // Entirely remove a single notification on the backend at a given ID.
  const deleteNotification = useCallback(
    async (id: string) => {
      await NotificationService.deleteNotification(id);
      await refreshNotifications();
      return true;
    },
    [refreshNotifications]
  );

  // Mark a notification as read on the backend at a given ID.
  const markNotificationRead = useCallback(
    async (id: string) => {
      await NotificationService.readNotification(id);
      await refreshNotifications();
      return true;
    },
    [refreshNotifications]
  );

  // Subscribe to recieve new notifications
  useEventSource(
    useMemo(
      () => ({
        url: "/noti/subscribe",
        userProfile: profileContext.currentProfile,
        onMessage: () => refreshNotifications(),
        onConnected: () => null,
        openWhenHidden: true
      }),
      [profileContext.currentProfile, refreshNotifications]
    )
  );

  // Checks if a notification's do not show array matches current page prop
  const matchesPageProperties = useCallback(
    (doNotShow: Array<DoNotShowForJob | DoNotShowForPage>) => {
      let matches = false;
      matches = doNotShow?.some((filter) => {
        let filterMatches = false;
        pageProperties?.map((property) => {
          if (isEqual(filter, property)) {
            filterMatches = true;
          }
        });
        return filterMatches;
      });
      return matches;
    },
    [pageProperties]
  );

  // get the unread notifications, filtered by supported properties.
  const getFilteredNotifications = useCallback(
    (currentJobId?: string) => {
      let filteredNotifications = unreadNotifications;
      // After this duration, a notification will never again show a bubble
      const expireDurationMS = 60000;
      if (currentJobId) {
        filteredNotifications = unreadNotifications.filter((notification) => {
          let keepNotification = true;
          if (matchesPageProperties(notification.do_not_show)) {
            markNotificationRead(notification.id);
            keepNotification = false;
          }
          if (
            new Date(Date.now()).getTime() -
              new Date(notification.time_created).getTime() >
            expireDurationMS
          ) {
            keepNotification = false;
          }
          return keepNotification;
        });
      }
      return filteredNotifications;
    },
    [markNotificationRead, matchesPageProperties, unreadNotifications]
  );

  // Always memoize values passed to context providers to avoid flooding.
  const context = useMemo(
    () => ({
      allNotifications,
      unreadNotifications,
      refreshNotifications,
      deleteNotification,
      markNotificationRead,
      startNativeNotifications,
      sendNativeNotification,
      getFilteredNotifications,
      setPageProperties
    }),
    [
      allNotifications,
      unreadNotifications,
      refreshNotifications,
      deleteNotification,
      markNotificationRead,
      startNativeNotifications,
      sendNativeNotification,
      getFilteredNotifications,
      setPageProperties
    ]
  );

  return (
    <NotificationContext.Provider value={context}>
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotificationContext = () => {
  return useContext(NotificationContext);
};
